import { DOCUMENT } from '@angular/common';
import { Component, HostListener, inject, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import {
  ActivatedRoute,
  NavigationEnd,
  NavigationStart,
  Router,
  RouterOutlet,
} from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { TranslateService } from '@ngx-translate/core';
import {
  AuthenticationService,
  GetSelectedAppId,
  GetUserId,
  IdleUserService,
  IsTokenExpired,
  redirectIfSessionExpired,
  resolveRedirectTarget,
  User,
  UserProvider,
} from '@siat-fe/auth';
import { LoadingStore, UpdateService } from '@siat-fe/core';
import { AppBarService } from '@siat-fe/layout';
import {
  CustomModalOptions,
  LoadingSpinnerComponent,
  ModalType,
  SmartModalAction,
  SmartModalService,
} from '@siat-fe/shared';
import {
  catchError,
  EMPTY,
  filter,
  map,
  mergeMap,
  Observable,
  tap,
  timeout,
} from 'rxjs';

@UntilDestroy()
@Component({
  imports: [RouterOutlet, LoadingSpinnerComponent],
  selector: 'app-root',
  templateUrl: './app.html',
  styleUrl: './app.scss',
})
export class App implements OnInit {
  /**
   * streams whether a global http loading is in progress.
   */
  httpLoading$: Observable<boolean>;

  /**
   * injected services.
   */
  loadingStore = inject(LoadingStore);
  router = inject(Router);
  idleUserService = inject(IdleUserService);
  smartModalService = inject(SmartModalService);
  userProvider = inject(UserProvider);
  authService = inject(AuthenticationService);
  translate = inject(TranslateService);
  activatedRoute = inject(ActivatedRoute);
  appBarService = inject(AppBarService);
  document = inject(DOCUMENT);
  private matIconRegistry = inject(MatIconRegistry);
  private domSanitizer = inject(DomSanitizer);

  /**
   * configures translations and registers custom icons.
   */
  constructor() {
    // created here, not in the app bar, so a VERSION_READY sent before login is not lost.
    inject(UpdateService);

    this.translate.addLangs(['it', 'en']);
    this.translate.setFallbackLang('en').subscribe();
    this.translate.use('it');
    this.updateHtmlLangAttribute(this.translate.getCurrentLang() || 'it');
    this.translate.onLangChange
      .pipe(untilDestroyed(this))
      .subscribe((event) => {
        this.updateHtmlLangAttribute(event.lang);
      });

    this.registerCustomIcons();
  }

  /**
   * @inheritdoc
   */
  ngOnInit(): void {
    this.setupServiceWorkerNavigationListener();
    this.checkRouterState();
    this.resetGoBackUrlOnPageChange();

    if (this.userProvider.isAuthenticated() && !IsTokenExpired()) {
      const userId = GetUserId();

      this.authService
        .getUserInfo(userId, true)
        .pipe(
          untilDestroyed(this),
          tap((user: User) => {
            const appId = GetSelectedAppId();
            this.userProvider.setCurrentUser(user);
            this.userProvider.setIsAdmin(user?.administrator || false);
            if (user?.strutture?.length) {
              this.userProvider.setStruttureList(user.strutture);
            }

            if (appId === 'amministrazione') {
              const { strutturaAdmin } = user;
              const { permessiVisivi } = strutturaAdmin;

              this.userProvider.setSelectedStruttura(strutturaAdmin);
              this.userProvider.setPermissionList(permessiVisivi);
            }
          }),
          catchError(() => {
            this.router.navigate(['/logout'], {
              queryParams: {
                sessionExpired: true,
                redirectTo:
                  resolveRedirectTarget(
                    window.location.pathname + window.location.search,
                  ) ?? undefined,
              },
              replaceUrl: true,
            });
            return EMPTY;
          }),
        )
        .subscribe();
    }
    this.checkUserInactivity();
  }

  /**
   * re-checks the token's own expiry whenever the tab regains focus, catching an expiry missed while away.
   */
  @HostListener('window:focus')
  onWindowFocus(): void {
    redirectIfSessionExpired(this.router, this.userProvider);
  }

  /**
   * listens for service worker push-navigation messages and routes to the requested page.
   */
  private setupServiceWorkerNavigationListener(): void {
    if (typeof window === 'undefined' || !('serviceWorker' in navigator)) {
      return;
    }

    navigator.serviceWorker.addEventListener('message', (event) => {
      const data = event?.data as { type?: string; route?: string } | undefined;
      if (data?.type !== 'PUSH_NAVIGATE') {
        return;
      }
      const route =
        typeof data.route === 'string' && data.route.trim().length > 0
          ? data.route
          : '/home/notifiche';
      this.router.navigateByUrl(route);
    });
  }

  /**
   * registers the app's custom svg icons.
   */
  private registerCustomIcons(): void {
    const icons = [
      'add_properties',
      'export',
      'new_job',
      'chart_people',
      'registration',
      'workflow',
    ];

    icons.forEach((icon) => {
      this.matIconRegistry.addSvgIcon(
        icon,
        this.domSanitizer.bypassSecurityTrustResourceUrl(
          `assets/icons/${icon}.svg`,
        ),
      );
    });
  }

  /**
   * updates the html lang attribute for accessibility.
   */
  updateHtmlLangAttribute(lang: string): void {
    this.document.documentElement.lang = lang;
  }

  /**
   * shows the idle confirmation modal whenever the user goes idle while authenticated.
   */
  checkUserInactivity(): void {
    this.idleUserService.userInactive
      .pipe(
        untilDestroyed(this),
        filter(
          (isIdle: boolean) => !!isIdle && this.userProvider.isAuthenticated(),
        ),
        tap(() => this.showIdleModal()),
      )
      .subscribe();
  }

  /**
   * clears the app bar back url when navigating to a different page, so a page's custom back url does not leak into the next one.
   */
  resetGoBackUrlOnPageChange(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        map((event: NavigationStart) => event.url.split('?')[0]),
        filter((path) => path !== this.router.url.split('?')[0]),
        untilDestroyed(this),
      )
      .subscribe(() => this.appBarService.setGoBackToUrl(''));
  }

  /**
   * updates the app bar title/back-button state from the active route's data on each navigation.
   */
  checkRouterState(): void {
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationEnd),
        map(() => this.activatedRoute),
        map((route) => {
          while (route.firstChild) {
            route = route.firstChild;
          }
          return route;
        }),
        filter((route) => route.outlet === 'primary'),
        mergeMap((route) => route.data),
      )
      .subscribe((event) => {
        this.appBarService.setTemplate(null);
        this.appBarService.setTitle(event['title'] || 'DEFAULT_TITLE_KEY');
        this.appBarService.setShowBackButton(event['showBackButton'] === true);
      });
  }

  /**
   * asks the user to confirm they're still there, refreshing the token on yes and logging out on no/timeout.
   */
  showIdleModal() {
    const idleModal = {
      disableHeader: true,
      message: 'Sei ancora tu?',
      closeButtonLabel: 'Si',
      dismissButtonLabel: 'No',
      config: {
        disableClose: true,
        hasBackdrop: true,
      },
    } as CustomModalOptions;

    this.smartModalService
      .openSmartModal(ModalType.CONFIRM, idleModal)
      .pipe(
        untilDestroyed(this),
        timeout(10000),
        catchError(() => {
          this.smartModalService.closeActiveSmartModal();
          this.router.navigate(['/logout'], {
            queryParams: { sessionExpired: true, redirectTo: this.router.url },
            replaceUrl: true,
          });
          return EMPTY;
        }),
        tap((result: SmartModalAction) => {
          if (result === SmartModalAction.CONFIRMED) {
            this.authService
              .refreshToken()
              .pipe(untilDestroyed(this))
              .subscribe((credentials) =>
                this.userProvider.setCredentials(credentials),
              );
          } else {
            this.router.navigate(['/logout'], {
              queryParams: {
                sessionExpired: true,
                redirectTo: this.router.url,
              },
              replaceUrl: true,
            });
          }
        }),
      )
      .subscribe();
  }
}
